import { Ref, ref, unref, watch, computed } from 'vue'
import {
  uploadInvoicesAPI,
  getPurchases,
  getDetail,
  removeInvoice,
  globalData,
  years,
  type Filters,
  Pagination,
  Sorting,
  getPaymentMethodsAPI,
  updateInvoiceAPI,
  setDeductibleProductAPI,
  getAllCategories,
  cleanPurchases,
} from '../../../data/pages/purchase'
import { PaymentMethod } from '../../../models/invoice'
import { watchIgnorable } from '@vueuse/core'
import { useSettings } from '../../settings/composables/useSettings'
const makePaginationRef = () => ref<Pagination>({ page: 1, perPage: 50, total: 0 })
const makeSortingRef = () => ref<Sorting>({ sortBy: 'date', sortingOrder: null })
const makeFiltersRef = () =>
  ref<Partial<Filters>>({
    isActive: true,
    search: '',
  })
export const usePurchases = (options?: {
  pagination?: Ref<Pagination>
  sorting?: Ref<Sorting>
  filters?: Ref<Partial<Filters>>
}) => {
  const isLoading = ref(false)
  const purchases = ref<any[]>([])
  const otherPurchases = ref<any[]>([])
  const isOtherPurchases = ref(false)
  const { filters = makeFiltersRef(), sorting = makeSortingRef(), pagination = makePaginationRef() } = options || {}
  const { settings } = useSettings()
  const months = ref([
    {
      id: '-1',
      name: 'Todos',
    },
    {
      id: '01',
      name: 'Enero',
    },
    {
      id: '02',
      name: 'Febrero',
    },
    {
      id: '03',
      name: 'Marzo',
    },
    {
      id: '04',
      name: 'Abril',
    },
    {
      id: '05',
      name: 'Mayo',
    },
    {
      id: '06',
      name: 'Junio',
    },
    {
      id: '07',
      name: 'Julio',
    },
    {
      id: '08',
      name: 'Agosto',
    },
    {
      id: '09',
      name: 'Septiembre',
    },
    {
      id: '10',
      name: 'Octubre',
    },
    {
      id: '11',
      name: 'Noviembre',
    },
    {
      id: '12',
      name: 'Diciembre',
    },
  ])
  const categories = ref([])
  const paymentMethods = ref<PaymentMethod[]>([])
  const fetch = async () => {
    isLoading.value = true
    isOtherPurchases.value = false
    const { data, pagination: newPagination } = await getPurchases({
      ...unref(filters),
      ...unref(sorting),
      ...unref(pagination),
    })
    purchases.value = data
    ignoreUpdates(() => {
      pagination.value = newPagination
    })
    isLoading.value = false
  }

  const fetchPurchases = async () => {
    isOtherPurchases.value = true
    isLoading.value = true

    const { data, pagination: newPagination } = await getPurchases(
      {
        ...unref(filters),
        ...unref(sorting),
        ...unref(pagination),
      },
      true,
      categoryMap.value,
    )

    otherPurchases.value = data
    ignoreUpdates(() => {
      pagination.value = newPagination
    })
    isLoading.value = false
  }

  const { ignoreUpdates } = watchIgnorable([pagination, sorting], fetch, { deep: true })

  watch(
    filters,
    () => {
      // Reset pagination to first page when filters changed
      pagination.value.page = 1
      if (!isOtherPurchases.value) {
        fetch()
      } else {
        fetchPurchases()
      }
    },
    { deep: true },
  )
  const categoryMap = computed(() => {
    const map = new Map<number, string>()
    categories.value.forEach((c: any) => {
      map.set(c.id, c.label.toLowerCase())
    })
    return map
  })

  return {
    categoryMap,
    purchases,
    otherPurchases,
    isLoading,
    filters,
    sorting,
    pagination,
    fetch,
    fetchPurchases,
    settings,
    years,
    months,
    paymentMethods,
    categories,
    async add(data: any) {
      isLoading.value = true
      try {
        await uploadInvoicesAPI(data)
        globalData.value = null
        if (!isOtherPurchases.value) {
          await fetch()
        } else {
          await fetchPurchases()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },
    async update(data: any) {
      isLoading.value = true
      try {
        await updateInvoiceAPI(data)
        globalData.value = null
        if (!isOtherPurchases.value) {
          await fetch()
        } else {
          await fetchPurchases()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },

    async getInvoiceDetail(id: string) {
      return await getDetail(id)
    },
    async setDeductibleProduct(data: { detail?: string; cleanDetail?: string; deductible: boolean }) {
      return await setDeductibleProductAPI(data)
    },
    async remove(id: string) {
      isLoading.value = true
      try {
        await removeInvoice(id)
        if (!isOtherPurchases.value) {
          await fetch()
        } else {
          await fetchPurchases()
        }
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },
    async getPaymentMethods() {
      paymentMethods.value = await getPaymentMethodsAPI()
    },
    async getCategories() {
      isLoading.value = true
      try {
        const data = await getAllCategories()
        categories.value = data
      } catch (e) {
        throw e
      } finally {
        isLoading.value = false
      }
    },
    clean() {
      cleanPurchases()
    },
  }
}
